import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/store/useAppStore'
import { setSentryUser, clearSentryUser } from '@/lib/sentry'
import type { User } from '@/types'
import type { Session } from '@supabase/supabase-js'

// Track login sans PII — fire-and-forget
function trackLogin(userId: string) {
  void supabase.from('analytics_events').insert({
    user_id: userId,
    event_name: 'user_login',
    properties: {},
  })
}

// ─── Extension Chrome — session sync ─────────────────────────────────────────
// Remplacez cette valeur par l'ID réel de l'extension une fois chargée dans Chrome
// (chrome://extensions → ID de l'extension RecrutPro)
const EXTENSION_ID = import.meta.env.VITE_CHROME_EXTENSION_ID as string | undefined

function syncSessionToExtension(session: Session | null) {
  try {
    // `chrome` n'existe que dans les navigateurs avec l'API Chrome Extensions
    const chromeRuntime = (
      window as Window & { chrome?: { runtime?: { sendMessage?: (id: string, msg: unknown) => Promise<unknown> } } }
    ).chrome?.runtime

    if (!EXTENSION_ID || !chromeRuntime?.sendMessage) return

    if (session) {
      chromeRuntime.sendMessage(EXTENSION_ID, { type: 'SET_SESSION', session })
        .catch(() => { /* extension non installée ou ID incorrect */ })
    } else {
      chromeRuntime.sendMessage(EXTENSION_ID, { type: 'CLEAR_SESSION' })
        .catch(() => {})
    }
  } catch {
    // Silencieux — l'extension est optionnelle
  }
}

export function useAuth() {
  const { user, authLoading, setUser, setAuthLoading } = useAppStore()

  useEffect(() => {
    const safetyTimeout = setTimeout(() => setAuthLoading(false), 3000)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      // ⚠️ PAS async : le SDK Supabase attend la fin du callback avant de
      // résoudre signInWithPassword — un callback async qui fait des appels DB
      // bloque toute la chaîne et laisse isSubmitting=true indéfiniment.
      (event, session) => {
        if (event === 'INITIAL_SESSION') {
          if (session?.user) {
            // Sync session vers l'extension si elle est déjà connectée au démarrage
            syncSessionToExtension(session)
            // Fire-and-forget : pas d'await
            fetchUserProfile(session.user.id)
              .catch(() => {})
              .finally(() => {
                clearTimeout(safetyTimeout)
                setAuthLoading(false)
              })
          } else {
            setUser(null)
            clearTimeout(safetyTimeout)
            setAuthLoading(false)
          }
          return
        }

        if (event === 'SIGNED_IN' && session?.user) {
          const uid = session.user.id
          syncSessionToExtension(session)
          setSentryUser(uid)
          trackLogin(uid)
          fetchUserProfile(uid)
            .catch(() => {})
            .finally(() => setAuthLoading(false))
          // Retry 1 s plus tard si le trigger DB n'a pas encore créé le profil
          setTimeout(() => fetchUserProfile(uid).catch(() => {}), 1000)
          return
        }

        if (event === 'SIGNED_OUT') {
          syncSessionToExtension(null)
          clearSentryUser()
          setUser(null)
          setAuthLoading(false)
        }
      },
    )

    return () => {
      subscription.unsubscribe()
      clearTimeout(safetyTimeout)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Profil ───────────────────────────────────────────────────────────────

  async function fetchUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    if (error) throw error

    if (data) {
      setUser(data as User)
      return
    }

    // Pas de profil → auto-création depuis les user_metadata
    const { data: { user: authUser } } = await supabase.auth.getUser()
    if (!authUser) return

    const meta = authUser.user_metadata ?? {}
    const seed = {
      id: userId,
      email: authUser.email ?? '',
      full_name: (meta.full_name as string) ?? '',
      role: 'recruiter' as const,
      legal_status: (meta.legal_status as string) ?? 'micro_entreprise',
      ca_objective_monthly: (meta.ca_objective_monthly as number) ?? 10000,
      fixed_charges: (meta.fixed_charges as Record<string, number>) ?? { loyer: 1400, outils: 500, internet: 50 },
    }

    const { data: created, error: insErr } = await supabase
      .from('users')
      .upsert(seed, { onConflict: 'id' })
      .select()
      .single()

    if (!insErr && created) setUser(created as User)
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  async function signIn(email: string, password: string) {
    // signInWithPassword résout immédiatement après le /token
    // (le callback onAuthStateChange prend le relais en arrière-plan)
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    // Bloquer ProtectedRoute jusqu'à ce que SIGNED_IN charge le profil
    setAuthLoading(true)
    return data
  }

  async function signUp(email: string, password: string, profile: Partial<User>) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: profile,
        emailRedirectTo: `${window.location.origin}/app`,
      },
    })
    if (error) throw error

    if (data.user) {
      try {
        await supabase.from('users').insert({ id: data.user.id, email, ...profile })
      } catch {
        // 409 ou 401 → trigger DB a déjà créé le profil
      }
      // Ne pas bloquer le rendu si la confirmation email est requise (data.session === null).
      // Dans ce cas, aucun événement SIGNED_IN ne sera émis tant que l'utilisateur
      // n'a pas cliqué sur le lien — setAuthLoading(true) sans session rendrait
      // le spinner permanent et bloquerait PublicRoute indéfiniment.
      if (data.session) {
        setAuthLoading(true)
      }
    }
    return data
  }

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setAuthLoading(false)
  }

  async function updateProfile(updates: Partial<User>) {
    if (!user) return
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single()
    if (error) throw error
    setUser(data as User)
    return data
  }

  async function updateEmail(newEmail: string) {
    const { error } = await supabase.auth.updateUser({ email: newEmail })
    if (error) throw error
    // Also update in users table
    if (user) {
      await supabase.from('users').update({ email: newEmail }).eq('id', user.id)
      setUser({ ...user, email: newEmail })
    }
  }

  async function updatePassword(currentPassword: string, newPassword: string) {
    // Re-authenticate first
    const { data: { user: authUser } } = await supabase.auth.getUser()
    if (!authUser?.email) throw new Error('Utilisateur non connecté')
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: authUser.email,
      password: currentPassword,
    })
    if (signInError) throw new Error('Mot de passe actuel incorrect')
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
  }

  async function deleteAccount() {
    if (!user) return
    // Delete all user data first
    await supabase.from('candidates').delete().eq('user_id', user.id)
    await supabase.from('mandates').delete().eq('user_id', user.id)
    await supabase.from('users').delete().eq('id', user.id)
    // Sign out (Supabase Admin API required for actual auth deletion — this deactivates the session)
    await supabase.auth.signOut()
    setUser(null)
    setAuthLoading(false)
  }

  return { user, loading: authLoading, signIn, signUp, signOut, updateProfile, updateEmail, updatePassword, deleteAccount }
}
